import React, { useEffect, useState } from "react";
import {
  CircularProgress, Button, Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Checkbox, Avatar,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon   from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useSnackbar } from "notistack";
import moment from "moment";
import { get_admins_api, add_admin_api, update_admin_api, remove_admin_api } from "../../DAL/FlightSchools/FlightSchools";
import ConfirmationPopup from "../../components/GeneralComponents/ConfirmationPopup";

const PERMISSIONS = [
  { key: "manageStudents",    label: "Manage Students",    description: "Create, view, suspend, terminate students" },
  { key: "manageBilling",     label: "Manage Billing",     description: "Renew student accounts, view billing" },
  { key: "viewAnalytics",     label: "View Analytics",     description: "View usage analytics" },
  { key: "manageBranding",    label: "Manage Branding",    description: "Upload logo/favicon, change colors" },
  { key: "manageAISettings",  label: "Manage AI Settings", description: "Configure AI excess charging" },
  { key: "viewAuditLogs",     label: "View Audit Logs",    description: "View audit trail" },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const thSx = {
  color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase",
  letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif",
  borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4",
};

const dialogPaperSx = {
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "14px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
};

export default function FlightSchoolAdmins({ schoolId, school }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [admins,    setAdmins]    = useState([]);

  const defaultPerms = { manageStudents: true, manageBilling: false, viewAnalytics: false, manageBranding: false, manageAISettings: false, viewAuditLogs: false };
  const [addOpen,        setAddOpen]        = useState(false);
  const [addFormData,    setAddFormData]    = useState({ name: "", email: "", permissions: { ...defaultPerms } });
  const [addSubmitting,  setAddSubmitting]  = useState(false);

  const [editOpen,        setEditOpen]        = useState(false);
  const [editAdmin,       setEditAdmin]       = useState(null);
  const [editPermissions, setEditPermissions] = useState({});
  const [editSubmitting,  setEditSubmitting]  = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, admin: null });

  useEffect(() => { loadAdmins(); }, [schoolId]);

  const loadAdmins = async () => {
    try {
      const r = await get_admins_api(schoolId);
      if (r.code === 200) setAdmins(r.admins || []);
      else enqueueSnackbar(r.message || "Failed to load admins", { variant: "error" });
    } catch { enqueueSnackbar("Error loading admins", { variant: "error" }); }
    finally { setIsLoading(false); }
  };

  const handleAddAdmin = async () => {
    if (!addFormData.name.trim() || !addFormData.email.trim()) { enqueueSnackbar("Name and email are required", { variant: "error" }); return; }
    setAddSubmitting(true);
    try {
      const r = await add_admin_api(schoolId, addFormData);
      if (r.code === 200 || r.code === 201) {
        enqueueSnackbar("Admin added successfully", { variant: "success" });
        setAddOpen(false); setAddFormData({ name: "", email: "", permissions: { ...defaultPerms } }); loadAdmins();
      } else enqueueSnackbar(r.message || "Failed to add admin", { variant: "error" });
    } catch { enqueueSnackbar("Error adding admin", { variant: "error" }); }
    finally { setAddSubmitting(false); }
  };

  const handleSavePermissions = async () => {
    setEditSubmitting(true);
    try {
      const r = await update_admin_api(schoolId, editAdmin._id, { permissions: editPermissions });
      if (r.code === 200) {
        enqueueSnackbar("Permissions updated", { variant: "success" }); setEditOpen(false); setEditAdmin(null); loadAdmins();
      } else enqueueSnackbar(r.message || "Failed to update permissions", { variant: "error" });
    } catch { enqueueSnackbar("Error updating permissions", { variant: "error" }); }
    finally { setEditSubmitting(false); }
  };

  const handleDeleteAdmin = async () => {
    try {
      const r = await remove_admin_api(schoolId, deleteConfirm.admin._id);
      if (r.code === 200) { enqueueSnackbar("Admin removed", { variant: "success" }); loadAdmins(); }
      else enqueueSnackbar(r.message || "Failed to remove admin", { variant: "error" });
    } catch { enqueueSnackbar("Error removing admin", { variant: "error" }); }
    setDeleteConfirm({ open: false, admin: null });
  };

  const getPermissionChips = (permissions) => {
    if (!permissions) return null;
    const active = PERMISSIONS.filter((p) => permissions[p.key]);
    if (active.length === 0) return <Chip label="No permissions" size="small" sx={{ backgroundColor: "rgba(26,26,26,0.06)", color: "#aaa", fontFamily: "'Rubik', sans-serif" }} />;
    return active.slice(0, 3).map((p) => (
      <Chip key={p.key} label={p.label} size="small"
        sx={{ mr: 0.5, mb: 0.5, backgroundColor: "rgba(192,30,46,0.09)", color: "#c01e2e", fontSize: "11px", fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
    ));
  };

  const PermissionCheckboxes = ({ perms, onChange }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1 }}>
      {PERMISSIONS.map((perm) => (
        <FormControlLabel key={perm.key}
          control={<Checkbox checked={perms[perm.key] || false} onChange={(e) => onChange({ ...perms, [perm.key]: e.target.checked })}
            sx={{ color: "#aaa", "&.Mui-checked": { color: "#c01e2e" } }} />}
          label={
            <Box>
              <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{perm.label}</Typography>
              <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>{perm.description}</Typography>
            </Box>
          }
        />
      ))}
    </Box>
  );

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: "#c01e2e" }} /></Box>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>School Administrators</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
          sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
          Add Admin
        </Button>
      </Box>

      {/* Info banner */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: "10px", boxShadow: "none" }}>
        <Typography sx={{ color: "#2563eb", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
          Only the primary admin ({school?.primary_admin_email}) can manage staff admins. Staff admins have limited permissions based on their assigned roles.
        </Typography>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
        <Table>
          <TableHead>
            <TableRow>
              {["Admin", "Email", "Role", "Permissions", "Added", ""].map((h) => (
                <TableCell key={h} sx={{ ...thSx, ...(h === "" && { width: 80 }) }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin._id} sx={{ "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, backgroundColor: admin.is_primary ? "#c01e2e" : "#2563eb", fontSize: "13px" }}>
                      {admin.is_primary ? <AdminPanelSettingsIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>{admin.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{admin.email}</TableCell>
                <TableCell>
                  <Chip label={admin.is_primary ? "Primary Admin" : "Staff Admin"} size="small"
                    sx={{ backgroundColor: admin.is_primary ? "rgba(192,30,46,0.1)" : "rgba(37,99,235,0.1)", color: admin.is_primary ? "#c01e2e" : "#2563eb", fontSize: "11px", fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
                </TableCell>
                <TableCell>
                  {admin.is_primary
                    ? <Chip label="Full Access" size="small" sx={{ backgroundColor: "rgba(22,163,74,0.1)", color: "#16a34a", fontSize: "11px", fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
                    : getPermissionChips(admin.permissions)}
                </TableCell>
                <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                  {admin.createdAt ? moment(admin.createdAt).format("DD MMM YYYY") : "—"}
                </TableCell>
                <TableCell>
                  {!admin.is_primary && (
                    <Box sx={{ display: "flex", gap: 0.25 }}>
                      <IconButton size="small" onClick={() => { setEditAdmin(admin); setEditPermissions(admin.permissions || {}); setEditOpen(true); }}
                        sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                        <EditIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, admin })}
                        sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                        <DeleteIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {admins.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>No admins found</Typography>
        </Box>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>Add Staff Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth label="Name"  value={addFormData.name}  onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })} sx={inputSx} />
            <TextField fullWidth label="Email" type="email" value={addFormData.email} onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })} sx={inputSx} />
            <Typography sx={{ color: "#c01e2e", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mt: 1, fontFamily: "'Rubik', sans-serif" }}>Permissions</Typography>
            <PermissionCheckboxes perms={addFormData.permissions} onChange={(perms) => setAddFormData({ ...addFormData, permissions: perms })} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAdmin} disabled={addSubmitting}
            sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
            {addSubmitting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Add Admin"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>Edit Permissions — {editAdmin?.name}</DialogTitle>
        <DialogContent>
          <PermissionCheckboxes perms={editPermissions} onChange={setEditPermissions} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePermissions} disabled={editSubmitting}
            sx={{ backgroundColor: "#c01e2e", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
            {editSubmitting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Save Permissions"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationPopup openDelete={deleteConfirm.open} setOpenDelete={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        handleAgree={handleDeleteAdmin} title={`Are you sure you want to remove ${deleteConfirm.admin?.name} as an admin?`} />
    </Box>
  );
}
